import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '@env/environment';

export type NetworkQuality = 'excellent' | 'good' | 'deficient' | 'poor' | 'offline';

export interface NetworkStatus {
    isOnline: boolean;
    apiAvailable: boolean | null;
    quality: NetworkQuality;
    latency: number | null;
}

@Injectable({providedIn: 'root'})
export class NetworkStatusService implements OnDestroy {
    private readonly TEST_INTERVAL = 30000;
    private readonly REQUEST_TIMEOUT = 5000;
    private statusSubject = new BehaviorSubject<NetworkStatus>({
        isOnline: navigator.onLine,
        apiAvailable: null,
        quality: navigator.onLine ? 'good' : 'offline',
        latency: null
    });
    readonly status$: Observable<NetworkStatus> = this.statusSubject.asObservable();
    private isDestroyed = false;
    private abortController: AbortController | null = null;
    private nextTest: ReturnType<typeof setTimeout>;
    private readonly onOnline = () => this.runLatencyTest();
    private readonly onOffline = () => {
        clearTimeout(this.nextTest);
        this.abortController?.abort();
        this.abortController = null;
        this.statusSubject.next({isOnline: false, apiAvailable: null, quality: 'offline', latency: null});
    };

    constructor() {
        window.addEventListener('online', this.onOnline);
        window.addEventListener('offline', this.onOffline);
        this.runLatencyTest();
    }

    private async runLatencyTest(): Promise<void> {
        clearTimeout(this.nextTest);
        this.abortController?.abort();
        if (this.isDestroyed) { return; }
        if (!navigator.onLine) {
            this.onOffline();
            return;
        }
        const controller = new AbortController();
        this.abortController = controller;
        const timeout = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);
        const startTime = performance.now();
        try {
            // Este endpoint público pasa por la misma API y consulta la base de datos.
            const url = environment.endpointApi.replace(/\/$/, '') + '/public/version';
            const response = await fetch(url, {cache: 'no-store', signal: controller.signal, credentials: 'omit'});
            if (!response.ok) { throw new Error('API unavailable'); }
            const data = await response.json();
            if (typeof data?.version !== 'string') { throw new Error('Unexpected API response'); }
            if (this.abortController !== controller || this.isDestroyed) { return; }
            const latency = Math.round(performance.now() - startTime);
            this.statusSubject.next({isOnline: true, apiAvailable: true, quality: this.getQualityFromLatency(latency), latency});
        } catch {
            if (this.abortController !== controller || this.isDestroyed) { return; }
            this.statusSubject.next({isOnline: navigator.onLine, apiAvailable: false,
                quality: navigator.onLine ? 'poor' : 'offline', latency: null});
        } finally {
            clearTimeout(timeout);
            // Una petición reemplazada no debe programar otro ciclo de sondeo.
            if (this.abortController === controller) {
                this.abortController = null;
                if (!this.isDestroyed) { this.nextTest = setTimeout(() => this.runLatencyTest(), this.TEST_INTERVAL); }
            }
        }
    }

    private getQualityFromLatency(latency: number): NetworkQuality {
        if (latency <= 300) { return 'excellent'; }
        if (latency <= 800) { return 'good'; }
        if (latency <= 1500) { return 'deficient'; }
        return 'poor';
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        clearTimeout(this.nextTest);
        this.abortController?.abort();
        this.abortController = null;
        window.removeEventListener('online', this.onOnline);
        window.removeEventListener('offline', this.onOffline);
        this.statusSubject.complete();
    }
}
